import {VectorLayerDMTR} from "../../menu/view-menu/layer";
import * as olProj from "ol/proj";
import {Projection} from "ol/proj";
import {GritFeature} from "./grit.feature";
import {FeatureType} from "../EnumTypes";
import Point from "ol/geom/Point";
import {GritCoordinates, GritCoordinateType, RCS} from "./grit.coordinates";
import {GritCondition, GritConditionType} from "./grit.filter";
import {GritInputCondition, GritModel, GritOption, GritTableField} from "./grit.model";
import {Geometry} from "ol/geom";
import Circle from "ol/geom/Circle";
import GeoJSON from "ol/format/GeoJSON";
import {fromCircle} from "ol/geom/Polygon";
import {GritDynamicTableType} from "./grit.enums";
import {Sectorization} from "../sectorization";
import {GritUtils} from "../../utils/grit/GritUtils";
import {PowerField} from "./grit.variable-unit-fields";
import {main} from "@popperjs/core";
import { cloneDeep } from "lodash";
import { RadarModel } from "./grit.radar-model";

export enum DeviceSectorizationType{
  MAIN = "0",
  SIDE = "1",
  BACK = "2"
}

export class DeviceSectorization {
  power: PowerField = new PowerField();
  gain: number;
  type: DeviceSectorizationType;
  _minAngle:number;
  _maxAngle:number;

  selected: boolean;
  deleted: boolean;

  static getTypeByValue(value : string){
      return Object.entries(DeviceSectorizationType).find(([key, val]) => val === value.toString())?.[0];
  }

  toSectorization(doc: number, minAltitude: number, maxAltitude: number, direction: number = 0): Sectorization[] {
    
    const sectorization: Sectorization = new Sectorization();

    sectorization.id = DeviceSectorizationType[this.type];

    sectorization.doc = doc > 500 ? 500 : doc;

    sectorization.minaltitude = minAltitude;
    sectorization.maxaltitude = maxAltitude;

    // store angles signal before transforming them. For log porpuses.
    sectorization.isminanglenegative = (this.minAngle + direction) % 360 < 0;
    sectorization.ismaxanglenegative = (this.maxAngle + direction) % 360 <= 0;

    let positiveMinAngle: number = this.minAngle;
    let positiveMaxAngle: number = this.maxAngle;


    // transform negative angles in positive
    if (positiveMinAngle < 0) positiveMinAngle += 360;
    if (positiveMaxAngle < 0) positiveMaxAngle += 360;
    if (direction < 0) direction = 360 + direction;

    

    sectorization.minangle = (positiveMinAngle + direction) % 360;
    sectorization.maxangle = (positiveMaxAngle + direction) % 360;

    // max angle should always be greater than 0
    if (sectorization.maxangle == 0) {
      sectorization.maxangle = 360;
    }

    /*  if min angle is greater than max angle, as a result of transforming, the sectorization should be devided into two. 
        One from min angle to 360 and another from 0 to max angle */
    if (sectorization.minangle >= sectorization.maxangle) {
      const sectorizationsList: Sectorization[] = [];
      const secondSectorization = cloneDeep(sectorization);

      sectorization.maxangle = 360;
      sectorization.ishalf = true
      sectorizationsList.push(sectorization);
      
      secondSectorization.minangle = 0;
      secondSectorization.ishalf = true;
      sectorizationsList.push(secondSectorization);
      
      return sectorizationsList;
    }

    return [sectorization];
  }

  set minAngle(value: number){
    //this._minAngle = value % 360;
    this._minAngle = value;
  }
  get minAngle(){return this._minAngle;}

  set maxAngle(value: number){
    // this._maxAngle = value % 360;
    // // max angle should always be greater than 0
    // if (this._maxAngle == 0) {
    //   this._maxAngle = 360;
    // }

    this._maxAngle = value
  }
  get maxAngle(){return this._maxAngle;}


  public toDto(){
    return {
      type: this.type,
      power: this.power.dBW,
      gain: this.gain,
      minAngle: this.minAngle,
      maxAngle: this.maxAngle,
    }
  }

  public fromDto(dto: any){
    this.type = dto.type;
    this.power.dBW = dto.power;
    this.gain = dto.gain;
    this.minAngle = dto.minAngle;
    this.maxAngle = dto.maxAngle;

    return this;
  }
}

export class InterferenceTestDeviceModel implements GritModel<InterferenceTestDeviceModel>{
  id: number = 0;
  tsCreate: string;

  groupOriginator: string = "";

  name: string = "";
  latitude: GritCoordinates = new GritCoordinates(GritCoordinateType.Latitude);
  longitude: GritCoordinates = new GritCoordinates(GritCoordinateType.Longitude);
  direction: number = 0;

  maskRadius : number = 0;
  maskArea : string = "";
  maskGeometry : Geometry = undefined;

  device : DeviceModel = null;
  interferenceTestId: number;

  public toDTO() {
    return {
      id: this.id,
      tsCreate: this.tsCreate,

      groupOriginator: this.groupOriginator,

      name: this.name,
      latitude: this.latitude.value,
      longitude: this.longitude.value,

      device: JSON.stringify(this.device?.toDTO()),
      direction : this.direction,

      maskRadius: this.maskRadius,
      maskArea: this.maskArea,

      interferenceTestId: this.interferenceTestId,
    }
  }

  public fromDTO(itDeviceDTO: any) {
    this.id = itDeviceDTO.id;
    this.tsCreate = itDeviceDTO.tsCreate ?
      new Date(itDeviceDTO.tsCreate).toISOString().slice(0, 16) : "";

    this.groupOriginator = itDeviceDTO.groupOriginator;

    this.name = itDeviceDTO.name;
    this.latitude = new GritCoordinates(GritCoordinateType.Latitude, itDeviceDTO.latitude);
    this.longitude = new GritCoordinates(GritCoordinateType.Longitude, itDeviceDTO.longitude);

    this.maskRadius = itDeviceDTO.maskRadius;
    this.maskArea = itDeviceDTO.maskArea;

    this.device = itDeviceDTO.device != undefined ?
      new DeviceModel().fromDTO(JSON.parse(itDeviceDTO.device)) : null;
    this.direction = itDeviceDTO.direction;

    this.interferenceTestId = itDeviceDTO.interferenceTestId;

    return this;
  }

  //#region JSON
  public exportJson(){
    return {
      name: this.name,
      latitude: this.latitude.value,
      longitude: this.longitude.value,

      device: this.device.exportJson(),
      direction : this.direction,

      maskRadius: this.maskRadius,
    }
  }

  public importJson(itDeviceDTO : any, group : string, projection: Projection){
    this.groupOriginator = group;

    this.name = itDeviceDTO.name;
    this.latitude = new GritCoordinates(GritCoordinateType.Latitude, itDeviceDTO.latitude);
    this.longitude = new GritCoordinates(GritCoordinateType.Longitude, itDeviceDTO.longitude);

    this.maskRadius = itDeviceDTO.maskRadius;
    this.direction = itDeviceDTO.direction;

    this.device = new DeviceModel().importJson(itDeviceDTO.device, "NA");

    if (this.maskRadius > 0) this.defineHiddenArea(this.maskRadius * 1000, projection);

    return this;
  }
  //endregion

  public defineHiddenArea(radius: number = this.maskRadius, projection?: Projection) {
    if (this.maskRadius != 0 && radius != this.maskRadius * 1000) return;

    if(radius === 0){
      this.maskArea = null;
      this.maskGeometry = null;
      return
    }

    let r = radius * Math.sqrt(Math.random());
    let theta = Math.random() * 2 * Math.PI;
    let center = olProj.fromLonLat([this.longitude.value, this.latitude.value], RCS.EPSG_3857);
    let maskedCenter = [center[0] + r * Math.cos(theta), center[1] + r * Math.sin(theta)]

    maskedCenter = olProj.transform(maskedCenter, RCS.EPSG_3857, projection);

    this.maskArea = new GeoJSON().writeGeometry(fromCircle(new Circle(maskedCenter, RadarModel.getCircleRadiusForProjection(radius, projection)), 64), {
      dataProjection: 'CRS:84',
      featureProjection: projection.getCode(),
    })
  }

  public createFeature(layer: VectorLayerDMTR, projection: Projection): GritFeature<InterferenceTestDeviceModel> {
    if (this.maskArea != ""){
      this.maskGeometry = new GeoJSON().readGeometry(this.maskArea, {
        dataProjection: "CRS:84",
        featureProjection: projection.getCode(),
      })
    }

    let geometry : Geometry;
    if (this.latitude.value == 0 && this.longitude.value == 0 && this.maskGeometry != null){
      geometry = this.maskGeometry;
    }
    else{
      geometry = new Point(olProj.fromLonLat([this.longitude.value, this.latitude.value], projection))
    }

    return new GritFeature(this.id, {
      featureType: FeatureType.Device,
      layer: layer,
      geometry: geometry,
      entity: this
    })
  }

  public identifyToolOutput(){
    return `
        <h4 style="font-weight:bold">RFI-Transmitter</h4>
        <p>Name: ${this.name}</p>
        <p>Latitude: ${this.latitude.value}</p>
        <p>Longitude: ${this.longitude.value}</p>
        <p>Direction: ${this.direction}</p>
        <p>Mask Radius: ${this.maskRadius}</p>
        ${this.device.identifyToolOutput()}
        `
  }


  static configuration: Record<string, GritOption<any>[]> = {
    table: [
      {
        display: "Name",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return entity.name;
          }
        }
      },
      {
        display: "Latitude",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.latitude.value);
          }
        }
      },
      {
        display: "Longitude",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.longitude.value);
          }
        }
      },
      {
        display: "Direction[º]",
        value: {
          type: GritDynamicTableType.COMPLETE,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.direction);
          }
        }
      },
      {
        display: "Base RFI-Transmitter",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return entity.device?.name ?? "";
          }
        }
      },
      {
        display: "Type",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return entity.device?.type ?? "";
          }
        }
      },
      {
        display: "User Defined",
        value: {
          type: GritDynamicTableType.COMPLETE,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return entity.device?.userDefined ?? "";
          }
        }
      },
      {
        display: "Power[dBW]",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.device?.power.dBW);
          }
        }
      },
      {
        display: "Range[NM]",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.device?.range);
          }
        }
      },
      {
        display: "Frequency[GHz]",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.device?.frequency);
          }
        }
      },
      {
        display: "Vertical Gain[dBI]",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: InterferenceTestDeviceModel) {
            return String(entity.device?.verticalGain);
          }
        }
      },

    ] as GritOption<GritTableField<InterferenceTestDeviceModel>>[],

    filters: [] as GritOption<GritInputCondition>[],

    search: [
      {
        display: "All",
        value: {
          inputs: [],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("id", GritConditionType.StartsWith, "");
            return [condition]
          }
        }
      },
      {
        display: "Name",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("name", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Latitude",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("latitude.value", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Longitude",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("longitude.value", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Base RFI-Transmitter",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () =>  "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.name", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Frequency",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () =>  "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.frequency", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Range",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.range", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Direction",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.direction", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Power[dBW]",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.power.dBW", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
      {
        display: "Vertical Gain",
        value: {
          inputs: [
            {
              field: "Starts With",
              type: "text",
              value: () => "",
            },
          ],
          condition: function (values: string[]): GritCondition[] {
            let condition = new GritCondition("device.verticalGain", GritConditionType.StartsWith, values[0]);
            return [condition]
          }
        }
      },
    ] as GritOption<GritInputCondition>[]
  }
}

export class DeviceModel implements GritModel<DeviceModel> {
  id: number = 0;

  groupVisibility: string = "";
  groupAccess: string = "";
  groupOriginator: string = "";

  name: string = "";
  type: string = "";

  userDefined: string = "power"
  power: PowerField = new PowerField(0);
  range: number = 0;
  frequency: number = 0;
  verticalGain: number = 0;

  sectorizations: DeviceSectorization[] = [];

  antennaPolarisation = "linear";

  get polarisationLoss() { return this.antennaPolarisation == "linear" ? -3 : 0 };

  get frequencyHz() {return this.frequency * 1000 * 1000 * 1000}

  get mainType(){return this.type.split("/").at(0)}
  set mainType(mainType: string){
    this.type = DeviceModel.fieldsValues.type.find(entry => entry.value.startsWith(mainType)).value
  }

  get subType(){return this.type.split("/").at(1)}
  set subType(subType: string){
    this.type = DeviceModel.fieldsValues.type.find(entry => entry.value.endsWith(subType)).value
  }

  public toDTO() {
    return {
      id: this.id,

      groupVisibility: this.groupVisibility,
      groupAccess: this.groupAccess,
      groupOriginator: this.groupOriginator,

      name: this.name,
      type: this.type,

      userDefined: this.userDefined,
      power: this.power.dBW,
      range: this.range,
      frequency: this.frequency,
      verticalGain: this.verticalGain,
      antennaPolarisation: this.antennaPolarisation,

      sectorizations: JSON.stringify(this.sectorizations.map(sect => sect.toDto())),
    }
  }

  public fromDTO(deviceDTO: any) {
    this.id = deviceDTO.id;

    this.groupVisibility = deviceDTO.groupVisibility;
    this.groupAccess = deviceDTO.groupAccess;
    this.groupOriginator = deviceDTO.groupOriginator;

    this.name = deviceDTO.name;
    this.type = deviceDTO.type;

    this.userDefined = deviceDTO.userDefined;
    this.power.dBW = deviceDTO.power;
    this.range = deviceDTO.range;
    this.frequency = deviceDTO.frequency;
    this.verticalGain = deviceDTO.verticalGain;
    this.antennaPolarisation = deviceDTO.antennaPolarisation;

    this.sectorizations = (JSON.parse(deviceDTO.sectorizations ?? "[]")).map(sect => {
      return new DeviceSectorization().fromDto(sect)
    });

    return this;
  }

  //#region JSON
  public exportJson(){
    return {
      name: this.name,
      type: this.type,

      userDefined: this.userDefined,
      power: this.power.dBW,
      range: this.range,
      frequency: this.frequency,
      verticalGain: this.verticalGain,

      sectorizations: this.sectorizations.map(sect => sect.toDto()),
    }
  }


  public importJson(obj: any, group: string){
    this.groupVisibility = group;
    this.groupAccess = group;
    this.groupOriginator = group;

    this.name = obj.name;
    this.type = obj.type;

    this.userDefined = obj.userDefined;
    this.power.dBW = obj.power;
    this.range = obj.range;
    this.frequency = obj.frequency;
    this.verticalGain = obj.verticalGain;

    this.sectorizations = obj.sectorizations.map(sect =>  new DeviceSectorization().fromDto(sect));

    return this;
  }

  //endregion

  public identifyToolOutput(){
    return `
        <h4 style="font-weight:bold;margin-top: 5px;">Base RFI-Transmitter</h4>
        <p>Name: ${this.name}</p>
        <p>Type: ${this.type}</p>
        <p ${this.userDefined == "power" ? `style="font-weight:bold"` : ''}>Power[dBW]: ${this.power.dBW}</p>
        <p ${this.userDefined == "range" ? `style="font-weight:bold"` : ''}>Range: ${this.range}</p>
        <p>Frequency: ${this.frequency}</p>
        <p>Verical Gain: ${this.verticalGain}</p>
        `
  }

  static fieldsValues: Record<string, GritOption<string>[]> = {
    type: [
      {value: "Jammer/CW", display: "Jammer/CW"},
      {value: "Jammer/Noise", display: "Jammer/Noise"},
      {value: "Jammer/Pulse", display: "Jammer/Pulse"},
      {value: "Jammer/NA", display: "Jammer/NA"},
      {value: "Spoofer/NA", display: "Spoofer/NA"},
    ],
    maintype: [
      {value: "Jammer", display: "Jammer"},
      {value: "Spoofer", display: "Spoofer"},
    ],
  }

  static configuration: Record<string, GritOption<any>[]> = {
    table: [
      {
        display: "Name",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: DeviceModel) {
            return entity.name;
          }
        }
      },
      {
        display: "Type",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: DeviceModel) {
            return entity.type;
          }
        }
      },
      {
        display: "User Defined",
        value: {
          type: GritDynamicTableType.EXTENDED,
          fieldValue : function (entity: DeviceModel) {
            return entity.userDefined;
          }
        }
      },
      {
        display: "Power[dBW]",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: DeviceModel) {
            return String(entity.power.dBW);
          }
        }
      },
      {
        display: "Range[NM]",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: DeviceModel) {
            return String(entity.range);
          }
        }
      },
      {
        display: "Frequency[GHz]",
        value: {
          type: GritDynamicTableType.COMPLETE,
          fieldValue : function (entity: DeviceModel) {
            return String(entity.frequency);
          }
        }
      },
      {
        display: "Vertical Gain[dBI]",
        value: {
          type: GritDynamicTableType.BASIC,
          fieldValue : function (entity: DeviceModel) {
            return entity.verticalGain.toString();
          }
        }
      },

    ] as GritOption<GritTableField<DeviceModel>>[],

    filters: [] as GritOption<GritInputCondition>[],

    search: [] as GritOption<GritInputCondition>[]
  }
}
